// Listens to location and starts/stops the ActivityTracker on program content routes.
// Only starts when user is authenticated. Registers unload flush once.

import { useContext, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthProvider';
import {
  parseContextFromLocation,
  registerUnloadFlush,
  start,
  stop,
  pause,
  type ActivityContext,
} from '../services/activityTracker';

let unloadRegistered = false;

export function ActivityTrackerGate() {
  const location = useLocation();
  const user = useContext(AuthContext);
  const prevContextRef = useRef<ActivityContext | null>(null);

  useEffect(() => {
    if (!unloadRegistered) {
      registerUnloadFlush();
      unloadRegistered = true;
    }
  }, []);

  useEffect(() => {
    const ctx = parseContextFromLocation(location.pathname, location.search);
    const isAuthenticated = user?.is_authenticated === true;

    if (!isAuthenticated || !ctx) {
      stop();
      prevContextRef.current = null;
      return;
    }

    const prev = prevContextRef.current;
    const samePage =
      prev &&
      prev.programId === ctx.programId &&
      prev.contentId === ctx.contentId &&
      prev.studyGroupId === ctx.studyGroupId &&
      prev.trackId === ctx.trackId;

    if (!samePage) {
      stop();
      prevContextRef.current = ctx;
      start(ctx);
    }
  }, [location.pathname, location.search, user?.is_authenticated]);

  // Background tab / visibility: end session when hidden to avoid overcounting
  // (timers are throttled, activity events stop; when visible again, start new session).
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        pause();
        return;
      }
      if (document.visibilityState !== 'visible') return;
      const ctx = parseContextFromLocation(location.pathname, location.search);
      const isAuthenticated = user?.is_authenticated === true;
      if (isAuthenticated && ctx) {
        prevContextRef.current = ctx;
        start(ctx);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [location.pathname, location.search, user?.is_authenticated]);

  return null;
}
